import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import {
  getQuoteItemThumbnailUrl,
  resolveQuoteItemVariantColor,
} from 'src/shared/utils/quoteItemThumbnail'

export const firstTrimmedUrl = (
  urls: Array<string | null | undefined>
): string | null => {
  for (const url of urls) {
    if (typeof url === 'string' && url.trim()) return url.trim()
  }
  return null
}

export const itemMockupUrls = (item: QuoteItem_): string[] =>
  (item.quote_sub_item ?? [])
    .flatMap((sub) => sub.mockup_urls ?? [])
    .filter(
      (url): url is string => typeof url === 'string' && Boolean(url.trim())
    )

export const itemDecorationDesign = (item: QuoteItem_) =>
  (item.quote_sub_item ?? []).find(
    (sub) => sub.type === 'decoration' && sub.design?.[0]
  )?.design?.[0]

/**
 * True when the saved logo placement was made on a photo of another colour of
 * this product, i.e. the line's colour changed after the placement and its
 * mockups were made. A placement on a photo we cannot match to any colour is
 * kept as before.
 */
export const itemBrandingIsOtherColour = (item: QuoteItem_) => {
  const placedOn = itemDecorationDesign(item)?.image_url?.trim()
  if (!placedOn) return false
  const { chosen_product_variant_color, product_variant_colors } =
    resolveQuoteItemVariantColor(item)
  if (!chosen_product_variant_color) return false
  return product_variant_colors.some(
    (color) =>
      color.id !== chosen_product_variant_color.id &&
      (color.product_image ?? []).some(
        (image) => image?.image_url?.trim() === placedOn
      )
  )
}

/** A generated mockup, or the logo placed on the product photo. */
export const itemHasLogoVisual = (item: QuoteItem_) => {
  if (itemBrandingIsOtherColour(item)) return false
  if (itemMockupUrls(item)[0]) return true
  const design = itemDecorationDesign(item)
  return Boolean(
    firstTrimmedUrl([design?.design_url]) &&
      firstTrimmedUrl([design?.image_url, getQuoteItemThumbnailUrl(item)])
  )
}

export const calculateShippingCount = (
  quoteItems: QuoteItem_[] | undefined
): number => {
  if (!quoteItems) return 0
  return quoteItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
}

/** One decision on the proposal: a recommendation plus its alternatives. */
export interface ProposalLine {
  key: string
  /** quote_item.slot, or null for a line without alternatives. */
  slot: string | null
  options: QuoteItem_[]
  /** Accepted option if the customer approved, else Klayder's pick, else the first. */
  defaultOptionId: number
}

export type ProposalSelection = Record<string, number>
/** Customer-chosen quantity per line key (only lines they changed). */
export type ProposalQuantities = Record<string, number>

export const isProductQuoteItem = (item: QuoteItem_) =>
  item.type === 'product' || (!item.type && !!item.product)

/**
 * Groups product lines into decisions. Lines sharing a `slot` become one
 * decision; lines without a slot each stay their own single-option decision,
 * so older quotes render unchanged.
 */
export const groupProposalLines = (items: QuoteItem_[]): ProposalLine[] => {
  const sorted = [...items].sort((a, b) => a.id - b.id)
  const lines: ProposalLine[] = []
  const bySlot = new Map<string, ProposalLine>()

  sorted.forEach((item) => {
    const slot = item.slot?.trim() || null
    if (!slot) {
      lines.push({
        key: `item-${item.id}`,
        slot: null,
        options: [item],
        defaultOptionId: item.id,
      })
      return
    }
    const existing = bySlot.get(slot)
    if (existing) {
      existing.options.push(item)
      return
    }
    const line: ProposalLine = {
      key: `slot-${slot}`,
      slot,
      options: [item],
      defaultOptionId: item.id,
    }
    bySlot.set(slot, line)
    lines.push(line)
  })

  bySlot.forEach((line) => {
    const accepted = line.options.find((option) => option.customer_selected_at)
    const pick = line.options.find((option) => option.is_recommended)
    line.defaultOptionId = (accepted ?? pick ?? line.options[0]).id
  })

  return lines
}

/** Chosen option per line, falling back to the default. */
export const resolveSelection = (
  lines: ProposalLine[],
  selection: ProposalSelection
): ProposalSelection =>
  Object.fromEntries(
    lines.map((line) => {
      const chosen = selection[line.key]
      const valid = line.options.some((option) => option.id === chosen)
      return [line.key, valid ? chosen : line.defaultOptionId]
    })
  )

export const chosenOption = (
  line: ProposalLine,
  selection: ProposalSelection
): QuoteItem_ =>
  line.options.find((option) => option.id === selection[line.key]) ??
  line.options[0]

/** Line ids the customer is approving, for slotted lines only. */
export const selectedAlternativeIds = (
  lines: ProposalLine[],
  selection: ProposalSelection
): number[] =>
  lines
    .filter((line) => line.slot !== null)
    .map((line) => chosenOption(line, selection).id)

export const MIN_LINE_QUANTITY = 1
export const MAX_LINE_QUANTITY = 100000

/**
 * The line priced at the customer's quantity. Per-unit decoration that follows
 * the line quantity scales with it; one-off setup and other fixed lines don't.
 * Mirrors what approve_shared_quote_with_selection() writes on approval.
 */
export const withQuantity = (item: QuoteItem_, quantity: number): QuoteItem_ => {
  if (quantity === item.quantity) return item
  return {
    ...item,
    quantity,
    quote_sub_item: item.quote_sub_item?.map((sub) =>
      sub.type === 'decoration' && sub.quantity === item.quantity
        ? { ...sub, quantity }
        : sub
    ),
  }
}

const withCurrency = (value: string, currency: string) =>
  currency === 'SEK' ? `${value} kr` : `${value} ${currency}`

/** Totals: whole currency units. */
export const formatProposalMoney = (amount: number, currency: string) =>
  withCurrency(Math.round(amount).toLocaleString('sv-SE'), currency)

/** Unit prices keep up to 2 decimals so "qty × unit" adds up after currency conversion. */
export const formatProposalUnitPrice = (amount: number, currency: string) =>
  withCurrency(
    amount.toLocaleString('sv-SE', { maximumFractionDigits: 2 }),
    currency
  )

/**
 * Existing rule (Terms of Service): "Quotes are valid for 30 days unless the
 * quote says otherwise." No per-quote override exists in the schema yet.
 */
export const QUOTE_VALID_DAYS = 30

export const formatProposalDate = (date: Date, locale: string) =>
  date.toLocaleDateString(locale === 'sv' ? 'sv-SE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
  })

const DRAFT_STORAGE_PREFIX = 'proposal-draft:'

export interface ProposalDraft {
  selection: ProposalSelection
  quantities: ProposalQuantities
}

/**
 * The customer's unsaved choices, kept across the sign-in round trip. localStorage
 * (not sessionStorage) because the sign-in link may open in a new tab.
 */
/** Keeps only entries whose value passes `isValid`; anything else in storage is ignored. */
const pickValid = (
  value: unknown,
  isValid: (n: unknown) => n is number
): Record<string, number> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value as Record<string, unknown>).filter(
          (entry): entry is [string, number] => isValid(entry[1])
        )
      )
    : {}

const isLineId = (n: unknown): n is number =>
  typeof n === 'number' && Number.isSafeInteger(n) && n > 0

const isLineQuantity = (n: unknown): n is number =>
  typeof n === 'number' &&
  Number.isInteger(n) &&
  n >= MIN_LINE_QUANTITY &&
  n <= MAX_LINE_QUANTITY

export const readStoredDraft = (publicId: string): ProposalDraft => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_PREFIX + publicId)
    const parsed = raw ? JSON.parse(raw) : null
    return {
      selection: pickValid(parsed?.selection, isLineId),
      quantities: pickValid(parsed?.quantities, isLineQuantity),
    }
  } catch {
    return { selection: {}, quantities: {} }
  }
}

export const storeDraft = (publicId: string, draft: ProposalDraft) => {
  try {
    localStorage.setItem(DRAFT_STORAGE_PREFIX + publicId, JSON.stringify(draft))
  } catch {
    // Storage unavailable (private mode) — choices just won't survive a reload.
  }
}

export const clearStoredDraft = (publicId: string) => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_PREFIX + publicId)
  } catch {
    // ignore
  }
}
